/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import {
  Dispatch,
  Fragment,
  Ref,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import Table, { TableProps } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import { Badge, Tabs, Tooltip } from 'antd';
// local imports
import { useStyles } from '~src/hooks/useStyles';
import { idOrNull } from '~src/services/api';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { useProjectId } from '~src/hooks/useProjectId';
import {
  dbFileToTableEntry,
  dbTenderToTableEntry,
  sortByModified,
  sortByType,
  TableEntry,
} from '../../../atoms/FileTables/FileTable.utils';

import IconButton from '~components/_common/IconButton/IconButton';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import {
  DbFile,
  DbFocusOptions,
  DbModuleFile,
  DbShare,
  DbTenderSVFAttachment,
  TenderRegistrationVM,
} from '~src/types/db';

import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import { find, uniq, uniqBy } from 'lodash';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import { formatDate } from '~src/utils/formatDate';
import { UploadProps } from 'antd/lib/upload';

import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { usePendingQuestionsCount } from '~src/hooks/api-hooks/useQuestions';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { useTenders } from '~src/hooks/api-hooks/useTenders';
import FileTenderHammer from '~src/assets/nav-icons/file-tender-hammer.svg';
import FileTenderPublic from '~src/assets/nav-icons/file-tender-public.svg';
import FileTenderPublicRevoked from '~src/assets/nav-icons/file-tender-public-revoked.svg';
import { useIsPath } from '~src/hooks/useIsPath';
import { ShareTenderModal } from '~components/popups-modals/ShareTender.modal';
import { TenderDocumentsTable } from '~components/atoms/FileTables/TenderDocumentsTable';
import UploadContext from '~src/contexts/UploadContext';
import { FileX } from '~src/types/upload';
import ContractAndOffer from './components/ContractAndOffers/ContractAndOffer';
import TenderDetails from '~components/molecules/DetailsLayouts/TenderDetails';
import TenderQuestionBlock from './components/Questions/TenderQuestionBlock';
import { useTenderFolderRegistration } from '~src/hooks/api-hooks/useTenderFolderRegistration';
import RightSideContentWrapper from '~components/atoms/Wrappers/RightSideContentWrapper/RightSideContentWrapper';
import { OffersTab } from './components/Offers/OffersTab';
import { usePrintWithRef } from '~src/hooks/usePrint';
import TenderDocumentExpandable from '~components/molecules/RightSideView/components/TenderDocumentsPrint/TenderDocumentsPrint';
import { styleFn } from './TenderTabs.styles';
import { TenderSharesBlock } from './components/Shares/TenderSharesBlock';
import TenderActions from './components/TenderActions/TenderActions';
import MovementsBlock from '~components/molecules/Tabs/TenderTabs/components/Movements/MovementsBlock';
import { SharesHelpInfoModal } from '~components/popups-modals/SharesHelpInfo.modal';
import { QuestionAnswerHelpInfoModal } from '~components/popups-modals/QAHelpInfo.modal';
import { OffersTabNew } from '~components/molecules/Tabs/TenderTabs/components/OffersNew/OffersTabNew';
import { TenderOptionsModal } from '~components/popups-modals/TenderOptionsModal/TenderOptions.modal';
import { useStore } from '~src/store';
import { TenderFolderModal } from '~components/popups-modals/TenderFolderModal/TenderFolderModal';
import { appLog } from '~src/utils/log';
import { useDynamicHeightAndLockScroll } from '~src/hooks/useDynamicHeightAndLockScroll';
import {
  getPublicShareStatus,
  PublicShareStatus,
  publicShareStatusIntlId,
} from '~components/pages/PublicShare/PublicShare.util';
import { RightClickOverlay } from '~components/atoms/RightClickOverlay/RightClickOverlay';
import dataContextMenu from '~src/data/dataContextMenu';

export enum TenderTabKeys {
  // dashboard = 'dashboard',
  details = 'details',
  documents = 'documents',
  contract = 'contract',
  offers = 'offers',
  movements = 'movements',
  questions = 'questions',
  shares = 'shares',
}

export const DEFULT_PROJECT_TENDER_TAB_KEY = TenderTabKeys.details;

const publicShareStatusIcon: Record<PublicShareStatus, string> = {
  private: FileTenderHammer,
  publicated: FileTenderPublic,
  revoked: FileTenderPublicRevoked,
};

export type SelectedShareKeysProps = {
  selectedSharedKeys?: TableRowSelection<DbShare>['selectedRowKeys'];
  onSelectSharedKeys?: TableRowSelection<DbShare>['onSelect'];
  onSelectAllSharedKeys?: TableRowSelection<DbShare>['onSelectAll'];
};

export type TenderTabsProps = TableProps<TableEntry> &
  SelectedShareKeysProps & {
    selectedKeys?: TableRowSelection<TableEntry>['selectedRowKeys'];
    onSelect?: TableRowSelection<TableEntry>['onSelect'];
    onSelectAll?: TableRowSelection<TableEntry>['onSelectAll'];
    highlightedRow?: TableEntry;
    suMode?: boolean;
    setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
    handleRow: Required<TenderTabsProps>['onRow'];
    handleShareKeyTableRowClick: Required<TableProps<DbShare>>['onRow'];
    onEmptyOverlay?: () => void;
    focusOptions?: DbFocusOptions[];
    parentContainerHeight?: string;
    currentPermissionLevel: RIGHTS_LEVEL;
    bottomViewHeight?: number;
    // SVF Viewer
    openSVFTranscribe?: (
      fileToView: DbFile | DbModuleFile | DbTenderSVFAttachment
    ) => void;
    tenderAttachmentSVFStateById?: Record<string, number | undefined>;
  };

/** Default TenderTabs, used with folder navigation */
export const TenderTabs: React.FC<TenderTabsProps> = ({
  selectedKeys,
  selectedSharedKeys,
  onSelectSharedKeys,
  onSelectAllSharedKeys,
  onSelect,
  onSelectAll,
  highlightedRow,
  suMode,
  handleRow,
  handleShareKeyTableRowClick,
  setSelectedItems,
  onEmptyOverlay,
  focusOptions,
  parentContainerHeight,
  currentPermissionLevel,
  bottomViewHeight,
  // SVF Viewer
  openSVFTranscribe,
  tenderAttachmentSVFStateById,
  ...tableProps
}) => {
  const isAuthenticated = true;
  const history = useHistory();
  const { path, url } = useRouteMatch();
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const currentFolder = useCurrentFolder();
  const folderId = currentFolder?.id || null;
  const permissionLevel = currentFolder?.permission || 0;
  const projectId = useProjectId();

  const isOnTender = useIsPath('/tenders', { endsWith: true });
  const isOnShares = useIsPath('/shares', { endsWith: true });
  const isOnQA = useIsPath('/questions', { endsWith: true });

  const archiveSettings = useArchiveSettings();
  const tagsData = archiveSettings.data?.resTags;

  const { setUploadContext } = useContext(UploadContext);
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { setFileToView } = useFileViewerContext();

  const [shareTenderModal, setShareTenderModal] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [tenderOptionsModal, setTenderOptionsModal] = useState(false);

  const [isDocumentPrintExpanded, setIsDocumentPrintExpanded] = useState(false);
  const [isSharesPrintExpanded, setIsSharesPrintExpanded] = useState(false);
  const [isMovementsPrintExpanded, setIsMovementsPrintExpanded] = useState(
    false
  );
  const [isContractPrintExpanded, setIsContractPrintExpanded] = useState(false);
  const [isOfferPrintExpanded, setIsOfferPrintExpanded] = useState(false);

  const [isHelpSharesModalVisible, setHelpSharesModalVisible] = useState(false);
  const [isHelpQAModalVisible, setHelpQAModalVisible] = useState(false);
  const [tenderFolderModalState, setTenderFolderModalState] = useState(false);

  const [
    rightClickTenderRegistration,
    setRightClickTenderRegistration,
  ] = useState<TenderRegistrationVM | undefined>();

  const tenderIdMatch = window?.location?.pathname?.match(/tenders\/(\d+)/);
  const tenderId = tenderIdMatch ? String(tenderIdMatch[1]) : '';

  const pendingQuestionsQuery = usePendingQuestionsCount({
    projectId: projectId ? Number(projectId) : undefined,
    tenderId,
  });
  const pendingCount = pendingQuestionsQuery.data ?? 0;
  const filesQueryBaseKeys = {
    folderId: idOrNull(folderId),
    showDeleted: showDeleted,
    withVersions: true,
    before:
      window.sessionStorage.getItem('timeMachineDate') !== null
        ? Number(window.sessionStorage.getItem('timeMachineDate'))
        : undefined,
  };

  // root tender table filters
  const { tenderFilterStates, tenderIncludeDeletedState } = useStore();

  const handleTabChange = (key: string) => {
    history.push(`${url}/${key}`);
  };

  useEffect(() => {
    if (isOnTender) setFileToView(undefined);
  }, [isOnTender]);

  useEffect(() => {
    const currentUrl = history.location.pathname;
    const urlParts = currentUrl.split('/').filter((part) => part !== '');
    const lastPart = urlParts[urlParts.length - 1];
    const secondLastPart = urlParts[urlParts.length - 2];

    if (secondLastPart === 'tenders' && /^\d+$/.test(lastPart)) {
      handleTabChange(DEFULT_PROJECT_TENDER_TAB_KEY);
    }
  }, [history.location.pathname]);

  const {
    handlePrint: handleSharesToPrint,
    printRef: sharesPrintRef,
  } = usePrintWithRef({
    onBeforeGetContent: () => setIsSharesPrintExpanded(true),
    onAfterPrint: () => setIsSharesPrintExpanded(false),
  });
  const {
    handlePrint: handleDocumentsToPrint,
    printRef: documentsPrintRef,
  } = usePrintWithRef({
    onBeforeGetContent: () => {
      setIsDocumentPrintExpanded(true);
      appLog(documentsPrintRef);
    },
    onAfterPrint: () => setIsDocumentPrintExpanded(false),
  });
  const {
    handlePrint: handleMovementsToPrint,
    printRef: movementsPrintRef,
  } = usePrintWithRef({
    onBeforeGetContent: () => setIsMovementsPrintExpanded(true),
    onAfterPrint: () => setIsMovementsPrintExpanded(false),
  });
  const {
    handlePrint: handleContractToPrint,
    printRef: contractPrintRef,
  } = usePrintWithRef({
    onBeforeGetContent: () => setIsContractPrintExpanded(true),
    onAfterPrint: () => setIsContractPrintExpanded(false),
  });
  const {
    handlePrint: handleOfferToPrint,
    printRef: offerPrintRef,
  } = usePrintWithRef({
    onBeforeGetContent: () => setIsOfferPrintExpanded(true),
    onAfterPrint: () => setIsOfferPrintExpanded(false),
  });

  const { data: tenderRegistration } = useTenderFolderRegistration(
    { projectId: projectId?.toString() || '', tender_id: tenderId },
    { enabled: !!projectId && !!tenderId }
  );

  const filesQuery = useFiles(filesQueryBaseKeys, {
    enabled: !!currentFolder && permissionLevel > 1 && !isOnTender,
    onError: () =>
      // go to /archive
      history.replace(`/projects/${projectId}/archive`),
  });

  // JIRA: TV1-332
  const tendersQuery = useTenders({
    show_deleted: tenderIncludeDeletedState,
  });
  // const tendersQuery = useTenders();

  const contractId = tendersQuery.data?.find(
    ({ id }) => String(id) === tenderId
  )?.contract_id;

  // console.log(`contractId`, contractId, tendersQuery.data);

  const searchContext = useContext(SearchContext);

  const tenderStateById = useMemo(() => {
    const map = new Map<string, number>();
    (tendersQuery.data || []).forEach((t: any) => {
      if (t?.id !== undefined && t?.id !== null) {
        map.set(String(t.id), Number(t.state || 0));
      }
    });
    return map;
  }, [tendersQuery.data]);

  const getTenderStateValue = (record: TableEntry): number => {
    // try common ids in your table entries
    const idCandidates = [
      // @ts-ignore
      record?.dbFolder?.id,
      // @ts-ignore
      record?.dbTender?.id,
      // @ts-ignore
      record?.id,
      record?.key,
    ]
      .filter((v) => v !== undefined && v !== null)
      .map((v) => String(v));

    for (const id of idCandidates) {
      const state = tenderStateById.get(id);
      if (typeof state === 'number') return state;
    }

    // fallback if TableEntry already contains state
    // @ts-ignore
    return Number(record?.state || 0);
  };

  const getTenderStateLabel = (state: number): string => {
    switch (state) {
      case 1:
        return intl.formatMessage({
          id: 'tender_state_draft',
        });
      case 3:
        return intl.formatMessage({
          id: 'tender_state_active',
        });
      case 6:
        return intl.formatMessage({
          id: 'tender_state_closed',
        });
      case 7:
        return intl.formatMessage({
          id: 'tender_state_processed',
        });
      default:
        return intl.formatMessage({
          id: 'tender_state_unknown',
        });
    }
  };

  const foldersSource = useMemo(
    () =>
      (tendersQuery.data || [])
        /*  .filter((v) => {
          return v.parent_id === folderId;
        }) */
        .map(dbTenderToTableEntry),
    [tendersQuery.data, folderId]
  );
  const filesSource = useMemo(
    () =>
      (filesQuery.data || [])
        .filter((item) => item.state !== DB_STATE.PARTIAL)
        .map((item) => dbFileToTableEntry(item)),
    [filesQuery.data]
  );

  const filteredFolders = [
    ...foldersSource.filter((item) => {
      // JIRA: TV1 - 332;
      const tenderState = getTenderStateValue(item);
      if (!tenderIncludeDeletedState && tenderState > 6) {
        return null;
      }
      if (tenderState <= 6 && tenderFilterStates.length === 0) {
        return null;
      }
      if (tenderState <= 6 && tenderFilterStates.length > 0) {
        if (!tenderFilterStates.includes(tenderState)) {
          return null;
        }
      }

      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(item.dbTender?.global_serial, searchContext.query) ||
        filterByQuery(
          getTenderStateLabel(getTenderStateValue(item)),
          searchContext.query
        ) ||
        filterByQuery(
          item.dbFile?.versions &&
            item.dbFile?.versions[0] &&
            item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];

  const filteredFiles = [
    ...filesSource.filter((item) => {
      //getting index of version in [versions] array by checking fileVersionsContext and [all current versions] array
      //Array.prototype.findIndex() || gets the index of the first element in the array that satisfies the provided testing function
      const dynamicIndex = (item.dbFile?.versions || []).findIndex(
        (el) =>
          //provided testing function
          el.version === fileVersionsContext[item.id]?.version &&
          fileVersionsContext[item.id]?.version
      );
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          dynamicIndex > 0
            ? item.dbFile?.versions &&
                item.dbFile?.versions[dynamicIndex] &&
                item.dbFile?.versions[dynamicIndex].version
            : item.dbFile?.versions &&
                item.dbFile?.versions[0] &&
                item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];

  const getBackgroundRow = (record: TableEntry) => {
    const data =
      record.dbFolder && record.dbFolder.focus
        ? find(focusOptions, {
            value: record.dbFolder.focus,
          })
        : '';

    if (
      (record.key === highlightedRow?.key && data) ||
      record.disabled ||
      window.sessionStorage.getItem('appState') === 'timeMachine'
    )
      return {
        color: 'black',
        background: `${data ? '#' + data.color_hex_code_opacity : ''}`,
      };
    return {
      color: 'black',
      background: `${data ? '#' + data.color_hex_code : ''}`,
    };
  };

  const handleSelectedTenderContext = (record?: TableEntry) => {
    if (!record?.dbTender) {
      return setRightClickTenderRegistration(undefined);
    }

    setRightClickTenderRegistration(record.dbTender);
  };

  const getProjectTenderRegistrationContextItems = () => {
    if (!rightClickTenderRegistration) return [];

    return dataContextMenu.projectTenderRegistrationContext || [];
  };

  const handleProjectTenderRegistrationContextAction = (modal: string) => {
    const selectedTenderId = rightClickTenderRegistration?.id;

    if (!selectedTenderId) return;

    if (modal === 'tender-details') {
      history.push(`${url}/${selectedTenderId}/${TenderTabKeys.details}`);
      return;
    }

    if (modal === 'publication') {
      setTenderOptionsModal(true);
      return;
    }
  };

  const columns = useMemo<TableProps<TableEntry>['columns']>(
    () => [
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 30,
        align: 'center',
        render: (_type, record) => {
          const publicStatus = getPublicShareStatus(record.dbTender?.public);

          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <Tooltip
                title={intl.formatMessage({
                  id: publicShareStatusIntlId[publicStatus],
                })}>
                <span>
                  <NavIcon
                    customStyle={{
                      width: '25px',
                      height: '25px',
                      float: 'none',
                    }}
                    icon={publicShareStatusIcon[publicStatus]}
                  />
                </span>
              </Tooltip>
            ),
          };
        },
        sorter: sortByType,
      },
      {
        title: intl.formatMessage({ id: 'state' }),
        dataIndex: 'state',
        width: 50, // ⬅ slightly wider for stable alignment
        align: 'center',
        render: (_state, record) => {
          const stateValue = getTenderStateValue(record);
          const label = getTenderStateLabel(stateValue);

          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <div
                style={{
                  display: 'inline-block',
                  minWidth: 60,
                  textAlign: 'left',
                }}>
                {label}
              </div>
            ),
          };
        },
        sorter: (a, b) => getTenderStateValue(a) - getTenderStateValue(b),
      },
      {
        title: intl.formatMessage({ id: 'reference' }),
        dataIndex: 'reference',
        width: 50,
        align: 'center',
        render: (reference, record) => {
          const value = String(reference ?? '');

          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <Tooltip title={value} placement="topLeft">
                <div
                  style={{
                    display: 'inline-block',
                    width: '100%',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  {value || '-'}
                </div>
              </Tooltip>
            ),
          };
        },
        sorter: (a, b) =>
          String(a.reference ?? '').localeCompare(String(b.reference ?? '')),
      },
      {
        title: intl.formatMessage({ id: 'name' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        width: 180,
        render: (name, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <Fragment>
                {name}
                {record.favourite && (
                  <IconButton
                    icon={['fas', 'star']}
                    color="yellow"
                    backgroundOff
                  />
                )}
              </Fragment>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        width: 175,
        sorter: (a, b) => {
          if (!a.description && !b.description) return 0;
          if (!a.description) return -1;
          if (!b.description) return 1;
          return (a.description || '').localeCompare(b.description || '');
        },
        ellipsis: true,
        render: (node, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: node,
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'modified-date' }),

        dataIndex: 'updatedAt',
        sorter: sortByModified(fileVersionsContext),

        width: 50,
        align: 'center',
        render: (updatedAt, record) => {
          let modified = updatedAt;

          // if file, show "modified" of selected version
          if (record.dbFile?.versions) {
            modified =
              fileVersionsContext[record.id]?.updated_at ||
              record.dbFile.versions[0]?.updated_at;
          }
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: formatDate(modified, 'short'),
          };
        },
      },
      {
        title: (
          <Tooltip title={intl.formatMessage({ id: 'gs-long' })}>
            <span style={{ cursor: 'help' }}>
              {intl.formatMessage({ id: 'gs-short' })}
            </span>
          </Tooltip>
        ),
        dataIndex: 'global_serial',
        width: 30, // Set a small fixed width
        align: 'center',
        render: (global_serial: string, record: TableEntry) => {
          const value = global_serial || '';
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <Tooltip
                title={
                  value
                    ? `${intl.formatMessage({ id: 'gs-long' })}: ${value}`
                    : ''
                }>
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '25px', // Forces the "xx..." behavior
                    margin: '0 auto',
                  }}>
                  {value || '-'}
                </div>
              </Tooltip>
            ),
          };
        },
        sorter: (a, b) =>
          (a.global_serial || '').localeCompare(b.global_serial || ''),
      },
    ],
    [
      fileVersionsContext,
      intl,
      focusOptions,
      highlightedRow,
      tagsData,
      tenderStateById,
    ]
  );

  // disable selection (cherrypicker) if no RIGHTS_LEVEL.OPEN on current folder
  const selectionDisabled = permissionLevel < RIGHTS_LEVEL.OPEN;

  /** calculate !getCheckboxProps.disabled */
  const isRecordSelectDisabled = (record: TableEntry) => {
    if (window.sessionStorage.getItem('appState') === 'superUser') {
      return false;
    }

    // if root, use folder permission
    if (folderId === null && record.dbFolder) {
      return record.dbFolder.permission < RIGHTS_LEVEL.OPEN;
    }
    // @ts-ignore
    if (suMode && record.state === DB_STATE.REMOVED) {
      return false;
    }

    return selectionDisabled || record.disabled;
  };

  const handleSelectShift = (record: TableEntry, index?: number) => {
    if (selectedKeys && selectedKeys.length === 0) {
      return setSelectedItems((prevSelectedItems) => [
        record,
        ...prevSelectedItems,
      ]);
    }
    if (selectedKeys && selectedKeys.length > 0) {
      const selectedIndex = selectedKeys.findIndex(
        (element) => element === record.key
      );
      if (selectedIndex >= 0) {
        return setSelectedItems((prevItems) =>
          prevItems.filter((item) => item.key !== record.key)
        );
      }
      const tableKeys = getTableData().map((item) => item.key);

      const isAnySelectedInCurrentView = selectedKeys.some((el) =>
        tableKeys.includes(el as string)
      );
      if (!isAnySelectedInCurrentView) {
        return setSelectedItems((prevSelectedItems) => [
          record,
          ...prevSelectedItems,
        ]);
      }

      const lastIndexReversed = getTableData()
        .reverse()
        .findIndex((el) => selectedKeys.includes(el.key));

      const lastIndex = getTableData().findIndex(
        (el) => el.key === getTableData().reverse()[lastIndexReversed].key
      );
      const firstIndex = getTableData().findIndex((el) =>
        selectedKeys.includes(el.key)
      );

      if ((index as number) >= 0) {
        if ((index as number) < firstIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(index, firstIndex),
          ]);
        }

        if ((index as number) > lastIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(lastIndex + 1, (index as number) + 1),
          ]);
        }

        if ((index as number) > firstIndex && (index as number) < lastIndex) {
          return setSelectedItems((prevState) =>
            uniq([
              ...prevState,
              ...getTableData().slice(firstIndex, (index as number) + 1),
            ])
          );
        }
      }
    }
  };

  const getTableData = () => {
    return [...filteredFolders, ...filteredFiles];
  };

  const handleUploadChange: UploadProps['onChange'] = (info) => {
    setUploadContext((prevFiles) => {
      const file = info.fileList.find((v) => v.uid === info.file.uid)
        ?.originFileObj as FileX | undefined;
      if (!file) return prevFiles;

      return uniqBy(
        [file, ...prevFiles],
        (v) =>
          `${v.name}@${
            v.webkitRelativePath === v.name ? '' : v.webkitRelativePath
          }`
      );
    });
  };

  const triggerUpload = () => {
    const uploadInput = document.querySelector(
      '.ant-upload input[type="file"]'
    ) as HTMLInputElement;
    if (uploadInput) {
      uploadInput.click();
    } else {
      console.error('Upload input not found');
    }
  };

  const content = (
    <Fragment>
      <RightClickOverlay
        items={getProjectTenderRegistrationContextItems()}
        permissionLevel={currentPermissionLevel}
        selected={{}}
        onOverlayClose={() => setRightClickTenderRegistration(undefined)}
        onItemClick={(item) =>
          handleProjectTenderRegistrationContextAction(item.modal)
        }>
        <Table<TableEntry>
          css={[styles.table]}
          showSorterTooltip={false}
          className={`main-content-window ${
            window.sessionStorage.getItem('appState') === 'timeMachine' &&
            'timeMachine-table'
          } ${suMode && 'su-table'}`}
          pagination={false}
          bordered
          columns={columns}
          dataSource={[...filteredFolders, ...filteredFiles]}
          loading={
            filesQuery.isLoading ||
            tendersQuery.isLoading ||
            tendersQuery.isIdle
          }
          rowClassName={(record) => {
            let className = suMode ? 'su-row-table ' : '';
            if (window.sessionStorage.getItem('appState') === 'timeMachine') {
              className += 'timeMachine-row-table' + ' ';
            }
            if (record.key === highlightedRow?.key) {
              className += 'ant-table-row-selected' + ' ';
            }
            if (
              record.key === highlightedRow?.key &&
              record.dbFolder &&
              !record.dbFolder.focus
            ) {
              className += 'ant-table-row-selected' + ' ';
            }
            if (record.disabled) {
              className += 'unfinished' + ' ';
            }

            return className;
          }}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: selectedKeys,
            columnWidth: 25,
            renderCell: (type, record, element, node) => {
              return {
                props: {
                  style: getBackgroundRow(record),
                },
                children: node,
              };
            },
            onSelect,
            onSelectAll,
            getCheckboxProps: (record) => ({
              disabled: isRecordSelectDisabled(record),
            }),
          }}
          onRow={(record, index) => {
            const baseRow = handleRow(record, index);

            return {
              ...baseRow,
              onContextMenu: () => {
                handleSelectedTenderContext(record);
              },
              onClick: (event) => {
                if (event.shiftKey) {
                  return handleSelectShift(record, index);
                }

                const onClickAction = baseRow.onClick;
                if (onClickAction) {
                  return onClickAction(event);
                }
              },
            };
          }}
          {...tableProps}
        />
      </RightClickOverlay>
      <div
        onContextMenu={() => {
          setRightClickTenderRegistration(undefined);
          onEmptyOverlay && onEmptyOverlay();
        }}
        className="test-class"
        style={{ height: 'inherit', width: '100%' }}></div>
    </Fragment>
  );

  const getActiveTab = () => {
    const pathSegments = history.location.pathname.split('/').filter(Boolean);
    // Assuming the base URL for this component is something like /documents
    // and you have tabs like "dashboard", "details", "documents", "contract", and "movements"

    // Check for special cases like 'tender_folder' which should map to 'documents' tab
    const specialCases = {
      'tender-folders': TenderTabKeys.documents,
      // Add other special cases here
    };

    // First, check for direct mappings
    for (const segment of pathSegments) {
      if (TenderTabKeys[segment]) {
        return TenderTabKeys[segment];
      }
    }

    // Then, check for special cases
    for (const segment of pathSegments) {
      if (specialCases[segment]) {
        return specialCases[segment];
      }
    }

    // Default to the first tab if no other conditions are met
    return DEFULT_PROJECT_TENDER_TAB_KEY; // Adjust this based on your default tab
  };

  return (
    <Fragment>
      <div
        style={{
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}>
        {!isOnTender ? (
          <div
            style={{
              marginTop: '-1.5px',
              display: 'flex',
              justifyContent: 'space-between',
            }}>
            <Tabs
              defaultActiveKey="1"
              onChange={handleTabChange}
              activeKey={getActiveTab()}
              style={{
                width: '100%',
                paddingLeft: '10px',
              }}
              tabBarStyle={{
                // maxWidth: 'calc(100% - 230px)',
                overflowX: 'auto',
                overflowY: 'hidden',
              }}>
              {isAuthenticated && (
                <Fragment></Fragment>
                // <Tabs.TabPane
                //   tab={
                //     <span>
                //       {intl.formatMessage({ id: 'tab-dashboard' })}
                //     </span>
                //   }
                //   key={TenderTabKeys.dashboard}>
                //   <Switch>
                //     <Route path={`${path}/dashboard`}>
                //       {/* Content for Dashboard Tab */}
                //       Dashboard ...
                //     </Route>
                //   </Switch>
                // </Tabs.TabPane>
              )}
              <Tabs.TabPane
                tab={intl.formatMessage({ id: 'tab-details' })}
                key={TenderTabKeys.details}>
                <Switch>
                  <Route path={`${path}/details`}>
                    {/* Content for Details Tab */}
                    <ProjectTenderDetailsContent
                      projectId={projectId}
                      tenderId={tenderId}
                      tenderRegistration={tenderRegistration}
                    />
                  </Route>
                </Switch>
              </Tabs.TabPane>
              {isAuthenticated && (
                <Fragment>
                  <Tabs.TabPane
                    css={{ position: 'relative' }}
                    tab={intl.formatMessage({ id: 'tab-documents' })}
                    key={TenderTabKeys.documents}>
                    <div css={styles.printContainer}>
                      {!!projectId && !!tenderId && (
                        <TenderDocumentExpandable
                          projectId={String(projectId)}
                          tenderId={tenderId}
                          ref={documentsPrintRef}
                          isStartFetch={isDocumentPrintExpanded}
                        />
                      )}
                    </div>
                    <Switch>
                      <Route path={`${path}/documents`}>
                        <TenderDocumentsTable
                          {...tableProps}
                          highlightedRow={highlightedRow}
                          showDeleted={showDeleted}
                          tenderId={tenderId}
                          handleRow={handleRow}
                          setSelectedItems={setSelectedItems}
                          onSelect={onSelect}
                          onSelectAll={onSelectAll}
                          selectedKeys={selectedKeys}
                          currentPermissionLevel={currentPermissionLevel}
                          onUploadTrigger={triggerUpload}
                          onOpenTenderFolders={() =>
                            setTenderFolderModalState(true)
                          }
                          openSVFTranscribe={openSVFTranscribe}
                          tenderAttachmentSVFStateById={
                            tenderAttachmentSVFStateById
                          }
                        />
                      </Route>
                    </Switch>
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab={intl.formatMessage({ id: 'tab-contract-name' })}
                    key={TenderTabKeys.contract}>
                    <Switch>
                      <Route path={`${path}/contract`}>
                        <ContractAndOffer
                          projectId={projectId}
                          tenderId={tenderId}
                          ref={contractPrintRef}
                          isStartFetch={isContractPrintExpanded}
                          tenderRegData={tenderRegistration}
                        />
                      </Route>
                    </Switch>
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab={intl.formatMessage({ id: 'tab-movements' })}
                    key={TenderTabKeys.movements}>
                    <Switch>
                      <Route path={`${path}/movements`}>
                        <MovementsBlock
                          projectId={projectId}
                          tenderId={tenderId}
                          highlightedRow={highlightedRow}
                          tenderRegData={tenderRegistration}
                          ref={movementsPrintRef}
                          isStartFetch={isMovementsPrintExpanded}
                          setSelectedItems={setSelectedItems}
                          onSelect={onSelect}
                          onSelectAll={onSelectAll}
                          selectedKeys={selectedKeys}
                          bottomViewHeight={bottomViewHeight}
                        />
                      </Route>
                    </Switch>
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab={
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                        }}>
                        {intl.formatMessage({ id: 'tab-questions' })}
                        {pendingCount > 0 && (
                          <Badge
                            count={pendingCount}
                            style={{ backgroundColor: '#faad14' }}
                          />
                        )}
                      </span>
                    }
                    key={TenderTabKeys.questions}>
                    <Switch>
                      <Route path={`${path}/questions`}>
                        <TenderQuestionBlock
                          tenderId={tenderId}
                          projectId={projectId}
                        />
                      </Route>
                    </Switch>
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab={intl.formatMessage({ id: 'tab-shares' })}
                    key={TenderTabKeys.shares}>
                    <Switch>
                      <Route path={`${path}/shares`}>
                        {projectId && tenderId && (
                          <TenderSharesBlock
                            {...tableProps}
                            projectId={projectId}
                            tenderId={tenderId}
                            ref={sharesPrintRef}
                            highlightedRow={highlightedRow}
                            selectedSharedKeys={selectedSharedKeys}
                            onSelectSharedKeys={onSelectSharedKeys}
                            onSelectAllSharedKeys={onSelectAllSharedKeys}
                            setSelectedItems={setSelectedItems}
                            onSelect={onSelect}
                            onSelectAll={onSelectAll}
                            selectedKeys={selectedKeys}
                            bottomViewHeight={bottomViewHeight}
                            tenderRegData={tenderRegistration}
                          />
                        )}
                      </Route>
                    </Switch>
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab={intl.formatMessage({ id: 'tab-offers' })}
                    key={TenderTabKeys.offers}>
                    <Switch>
                      <Route path={`${path}/offers`}>
                        <RightSideContentWrapper>
                          <OffersTabNew
                            projectId={projectId}
                            tenderId={tenderId}
                            ref={offerPrintRef}
                            tenderRegData={tenderRegistration}
                          />
                        </RightSideContentWrapper>
                      </Route>
                    </Switch>
                  </Tabs.TabPane>
                </Fragment>
              )}
            </Tabs>
            <div css={styles.actionsContainer}>
              <TenderActions
                projectId={projectId}
                tenderId={tenderId}
                showDeleted={showDeleted}
                handleUploadChange={handleUploadChange}
                handleDocumentsToPrint={handleDocumentsToPrint}
                handleSharesToPrint={handleSharesToPrint}
                handleMovementsToPrint={handleMovementsToPrint}
                handleContractToPrint={handleContractToPrint}
                handleOfferToPrint={handleOfferToPrint}
                setShowDeleted={setShowDeleted}
                setShareTenderModal={setShareTenderModal}
                setTenderOptionsModal={setTenderOptionsModal}
                tenderFolderModalState={tenderFolderModalState}
                setTenderFolderModalState={setTenderFolderModalState}
                tenderData={tenderRegistration}
                currentPermissionLevel={currentPermissionLevel}
              />
              {/* HELP ICON REMOVED TASK */}
              {isOnShares && (
                <IconButton
                  icon={['far', 'info-circle']}
                  customStyle={{ fontSize: '130%' }}
                  containerStyle={{
                    marginLeft: 0,
                    color: '#7a829b',
                  }}
                  click={() => {
                    if (isOnShares) setHelpSharesModalVisible(true);
                    if (isOnQA) setHelpQAModalVisible(true);
                  }}
                  tooltip={() => {
                    if (isOnShares) {
                      return intl.formatMessage({ id: 'vendors_help_tooltip' });
                    }

                    if (isOnQA) {
                      return intl.formatMessage({ id: 'qa_help_tooltip' });
                    }
                  }}
                />
              )}
            </div>
            <TenderFolderModal
              tenderId={tenderId}
              visible={tenderFolderModalState}
              onCancel={() => setTenderFolderModalState(false)}
            />
            <SharesHelpInfoModal
              visible={isHelpSharesModalVisible}
              onClose={() => setHelpSharesModalVisible(false)}
              headerId="vendors_list_help_header"
            />
            <QuestionAnswerHelpInfoModal
              visible={isHelpQAModalVisible}
              onClose={() => setHelpQAModalVisible(false)}
              headerId="qa_help_header"
            />
            <ShareTenderModal
              tenderId={tenderId}
              visible={shareTenderModal}
              files={[]}
              onCancel={() => {
                setShareTenderModal(false);
              }}
              onDone={() => {
                setShareTenderModal(false);
              }}
            />
          </div>
        ) : (
          content
        )}
        {/*  </div> */}
        <TenderOptionsModal
          visible={tenderOptionsModal}
          tenderId={
            rightClickTenderRegistration?.id
              ? Number(rightClickTenderRegistration.id)
              : undefined
          }
          tenderRegistration={
            rightClickTenderRegistration?.id
              ? tendersQuery.data?.find(
                  (tender) =>
                    String(tender.id) ===
                    String(rightClickTenderRegistration.id)
                ) || rightClickTenderRegistration
              : tenderRegistration
          }
          onCancel={() => {
            setTenderOptionsModal(false);
            setRightClickTenderRegistration(undefined);
          }}
          onDone={() => {
            setTenderOptionsModal(false);
            setRightClickTenderRegistration(undefined);
          }}
        />
      </div>
    </Fragment>
  );
};

type ProjectTenderDetailsContentProps = {
  projectId?: number;
  tenderId: string;
  tenderRegistration?: TenderRegistrationVM;
};

const ProjectTenderDetailsContent: React.FC<ProjectTenderDetailsContentProps> = ({
  projectId,
  tenderId,
  tenderRegistration,
}) => {
  const {
    containerRef,
    dynamicHeight,
  } = useDynamicHeightAndLockScroll<HTMLDivElement>();

  return (
    <div
      ref={containerRef}
      style={{
        height: dynamicHeight,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}>
      <TenderDetails
        isTenderPage={true}
        projectId={String(projectId)}
        tenderId={tenderId}
        tenderData={tenderRegistration}
      />
    </div>
  );
};
